import { useEffect, useRef, useState } from "react";
import "../Modal.css";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const ModalCreateProject = ({ isVisible, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [VenueName, setVenueName] = useState("");
  const [VenueNotes, setVenueNotes] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedPricingValue, setSelectedPricingValue] = useState("");
  const [projectSelectDate, setProjectSelectedDate] = useState("");
  const [projectSelectedHour, setProjectSelectedHour] = useState("12");
  const [projectSelectedMinute, setProjectSelectedMinute] = useState("00");
  const [projectSelectedPeriod, setProjectSelectedPeriod] = useState("AM");
  const [projectSelectEndDate, setProjectSelectedEndDate] = useState("");
  const [projectSelectedEndHour, setProjectSelectedEndHour] = useState("12");
  const [projectSelectedEndMinute, setProjectSelectedEndMinute] =
    useState("00");
  const [projectSelectedEndPeriod, setProjectSelectedEndPeriod] =
    useState("AM");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postCode, setPostCode] = useState("");

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setVenueName("");
    setVenueNotes("");
    setPhoneNumber("");
    setSelectedPricingValue("");
    setProjectSelectedDate("");
    setProjectSelectedHour("12");
    setProjectSelectedMinute("00");
    setProjectSelectedPeriod("AM");
    setProjectSelectedEndDate("");
    setProjectSelectedEndHour("12");
    setProjectSelectedEndMinute("00");
    setProjectSelectedEndPeriod("AM");
    setAddress("");
    setCity("");
    setState("");
    setPostCode("");
  };

  const inputRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return; // Ensure the modal is visible

    if (!window.google) {
      console.error("Google Maps API not loaded");
      return;
    }

    if (inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: [
            "formatted_address",
            "geometry",
            "name",
            "address_components",
          ],
          strictBounds: false,
        }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) {
          console.warn("No details available for input: '" + place.name + "'");
          return;
        }

        const addressComponents = place.address_components;

        let postCode = "",
          state = "",
          city = "",
          address = place.formatted_address;
        const fullAddress = place.name ? `${place.name}, ${address}` : address;

        addressComponents.forEach((component) => {
          if (component.types.includes("postal_code")) {
            postCode = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
        });

        setAddress(fullAddress);
        setCity(city);
        setState(state);
        setPostCode(postCode);
      });

      console.log("Autocomplete initialized successfully");
    } else {
      console.error("Input ref is not attached to any element");
    }
  }, [isVisible]); // Add isVisible as a dependency

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
  
    try {
      // Show confirmation dialog before proceeding
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to submit this quote request?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, submit it!",
        cancelButtonText: "No, cancel",
      });
  
      if (!result.isConfirmed) {
        setLoading(false);
        return;
      }
  
      const generateSetCode = () => {
        return Math.random().toString(36).substr(2, 8).toUpperCase();
      };
  
      const setCode = generateSetCode();
  
      const [startYear, startMonth, startDay] = projectSelectDate.split("-");
      let startHour = parseInt(projectSelectedHour, 10);
      const startMinute = parseInt(projectSelectedMinute, 10);
  
      if (projectSelectedPeriod === "PM" && startHour < 12) startHour += 12;
      if (projectSelectedPeriod === "AM" && startHour === 12) startHour = 0;
  
      const combinedStartDate = new Date(
        Date.UTC(startYear, startMonth - 1, startDay, startHour, startMinute)
      );
  
      const [endYear, endMonth, endDay] = projectSelectEndDate.split("-");
      let endHour = parseInt(projectSelectedEndHour, 10);
      const endMinute = parseInt(projectSelectedEndMinute, 10);
  
      if (projectSelectedEndPeriod === "PM" && endHour < 12) endHour += 12;
      if (projectSelectedEndPeriod === "AM" && endHour === 12) endHour = 0;
  
      const combinedEndDate = new Date(
        Date.UTC(endYear, endMonth - 1, endDay, endHour, endMinute)
      );
  
      // Validate that the end date is not before the start date
      if (combinedEndDate < combinedStartDate) {
        toast.error("End date cannot be before the start date.");
        setLoading(false);
        return;
      }

      const formatDateWithoutSelect = (date) => {
        return date && date.toISOString() === "1970-01-01T00:00:00.000Z" ? "" : date;
      };
      
  
      const paymentData = {
        firstName,
        lastName,
        phoneNumber,
        email,
        VenueNotes,
        address,
        city,
        state,
        postCode,
        projectSelectStartDate: combinedStartDate,
        projectSelectEndDate: combinedEndDate,
        set: [
          {
            setCode,
            setTitle: VenueName,
            startDate: projectSelectDate ? formatDateWithoutSelect(combinedStartDate) : "",
            endDate: projectSelectEndDate ? formatDateWithoutSelect(combinedEndDate) : "",
            products: [],
          },
        ],
      };
  
      const response = await fetch("https://server-gs.vercel.app/save-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentData),
      });
  
      if (response.ok) {
        Swal.fire({
          title: "Success!",
          text: "Quote request sent successfully!",
          icon: "success",
          timer: 300, // Auto-close after 2 seconds
          timerProgressBar: true,
          showConfirmButton: false, // Hide "OK" button
        });
  
        resetForm();
        onClose();
          // Call the success callback if provided
        onSuccess();
      } else {
        throw new Error("Failed to save payment details.");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ""); // Remove all non-digit characters

    let formatted = "";
    if (cleaned.length <= 3) {
      formatted = cleaned;
    } else if (cleaned.length <= 6) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }

    return formatted;
  };

  const handleChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    setPhoneNumber(formatted);
  };

  

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-gray-100 rounded-2xl shadow-2xl w-[80%]  mx-5 sm:mx-auto overflow-hidden">
        <div className="flex justify-between items-center p-6 bg-blue-600">
          <h1 className="text-white text-2xl font-bold">Create a Project</h1>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-9 h-9 rounded-full flex justify-center items-center transition hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col justify-between"
          >
            <div className="grid grid-cols-2 gap-5 p-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Personal Details
                </h2>
                <div className="grid grid-cols-2 gap-5">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      required
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder=""
                    />
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder=""
                    />
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full lowercase p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder=""
                      required
                    />
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
      type="tel"
      value={phoneNumber}
      onChange={handleChange}
      maxLength={14}
      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
      placeholder="(123) 456-7890"
    />
                  </div>
                </div>
                <div className="mt-5 w-full">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Booking Details
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                    {/* Start Date and Time */}
                    <div className="grid grid-cols-2 gap-5 w-full">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={projectSelectDate}
                          onChange={(e) =>
                            setProjectSelectedDate(e.target.value)
                          }
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={projectSelectedHour}
                            onChange={(e) =>
                              setProjectSelectedHour(e.target.value)
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(
                              (hour) => (
                                <option
                                  key={hour}
                                  value={hour < 10 ? `0${hour}` : hour}
                                >
                                  {hour < 10 ? `0${hour}` : hour}
                                </option>
                              )
                            )}
                          </select>
                          <select
                            value={projectSelectedMinute}
                            onChange={(e) =>
                              setProjectSelectedMinute(e.target.value)
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            {Array.from({ length: 12 }, (_, i) => i * 5).map(
                              (minute) => (
                                <option
                                  key={minute}
                                  value={minute < 10 ? `0${minute}` : minute}
                                >
                                  {minute < 10 ? `0${minute}` : minute}
                                </option>
                              )
                            )}
                          </select>
                          <select
                            value={projectSelectedPeriod}
                            onChange={(e) =>
                              setProjectSelectedPeriod(e.target.value)
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* End Date and Time */}
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={projectSelectEndDate}
                          onChange={(e) =>
                            setProjectSelectedEndDate(e.target.value)
                          }
                          min={projectSelectDate} // Restrict end date to be on or after the start date
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={projectSelectedEndHour}
                            onChange={(e) =>
                              setProjectSelectedEndHour(e.target.value)
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(
                              (hour) => (
                                <option
                                  key={hour}
                                  value={hour < 10 ? `0${hour}` : hour}
                                >
                                  {hour < 10 ? `0${hour}` : hour}
                                </option>
                              )
                            )}
                          </select>
                          <select
                            value={projectSelectedEndMinute}
                            onChange={(e) =>
                              setProjectSelectedEndMinute(e.target.value)
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            {Array.from({ length: 12 }, (_, i) => i * 5).map(
                              (minute) => (
                                <option
                                  key={minute}
                                  value={minute < 10 ? `0${minute}` : minute}
                                >
                                  {minute < 10 ? `0${minute}` : minute}
                                </option>
                              )
                            )}
                          </select>
                          <select
                            value={projectSelectedEndPeriod}
                            onChange={(e) =>
                              setProjectSelectedEndPeriod(e.target.value)
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Venue / Delivery Location
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Venue Name
                      </label>
                      <input
                        type="text"
                        value={VenueName}
                        onChange={(e) => setVenueName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Type..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Venue Notes
                      </label>
                      <input
                        type="text"
                        value={VenueNotes}
                        onChange={(e) => setVenueNotes(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Type..."
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Location Details
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Google map location
                      </label>
                      <input
                        ref={inputRef}
                        className="p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all w-full"
                        placeholder="Enter a location"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address
                        </label>
                        <input
                          type="text"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="Enter address"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="Enter city"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State
                        </label>
                        <input
                          type="text"
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="Enter state"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Post Code
                        </label>
                        <input
                          type="text"
                          value={postCode}
                          onChange={(e) => setPostCode(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="Enter post code"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 flex justify-start bg-gray-50 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="w-56 py-3 font-roboto  bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"

>
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModalCreateProject;
